import { useState } from 'react';
import { useRoute } from '@react-navigation/native';
import { deletePrompt, download, isPhone, isWeb, shareFile } from '@helpers';
import {
  OP_DOCUMENTS_DISPLAY_MODE,
  THREE_D_DRAWING_EXTENSIONS_TO_DISPLAY,
} from '../../../../../constants/operatorPanel.constants';
import { useTabCount } from '@v2/pages/common/hooks/useTabCount';
import { OperatorPanelInformationDocumentsProps } from '@components/operatorPanel/components/OperatorPanelInformation/components/OperatorPanelInformationDocuments/OperatorPanelInformationDocuments';
import { useSelector } from 'react-redux';
import { selectCurrentImageDocumentContext } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { ImageHandlingDisplayMode } from '@v2/components/imageHandling/types/types';
import { useImageHandlingViewModel } from '@v2/components/imageHandling/viewmodels/useImageHandlingViewModel';
import i18n from 'i18n-js';
import { useManageDocTypeButton } from '@v2/components/imageHandling/components/Header/components/DocTypeButton/hooks/useManageDocTypeButton';
import { HeaderIconsProps } from '@v2/components/imageHandling/components/Header/components/HeaderIcons';
import { ImageHandlingPopupItem } from '@v2/components/imageHandling/components/Header/components/ImageHandlingPopup';
import { OP_INFO_TABS_WIDTH } from '@v2/pages/common/constants/base.constants';
import { scale as initialScale } from '@components/frame';
import { useManageOPInformationTabsCrossIcon } from '@components/operatorPanel/hooks/useManageOPInformationTabsCrossIcon';

export type GetActionsConfig = (params: Partial<Pick<HeaderIconsProps, 'toggleAttachModal'>>) => ImageHandlingPopupItem[];

interface UseManageOPInformationDocumentsParams
  extends Pick<OperatorPanelInformationDocumentsProps, 'images' | 'C' | 'scale'> {}

export const useManageOPInformationDocuments = ({
  images,
  C,
  scale = initialScale,
}: UseManageOPInformationDocumentsParams) => {
  const route = useRoute();

  const imageContext = useSelector(selectCurrentImageDocumentContext);

  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const initialMode = route.params?.mode;
  const [mode, setMode] = useState<OP_DOCUMENTS_DISPLAY_MODE | ImageHandlingDisplayMode>(initialMode);

  const {
    actions: { cropImageDocument, rotateImageDocument, removeImageDocument },
  } = useImageHandlingViewModel({ imageContext });

  const [containerMeasurements, setContainerMeasurements] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [attachBlockMeasurements, setAttachBlockMeasurements] = useState<number>(0);

  const { onSuccessImageUpload, onSuccessDocTypeChange, onSuccessImageRemove } = useTabCount();

  const { onCrossIconPress } = useManageOPInformationTabsCrossIcon();

  const IMAGE_PADDING_HORIZONTAL = 12;
  const IMAGE_PADDING_TOP = 12;

  const selectedImage = images?.[selectedIndex];

  const theme = C.scheme === 'light' ? 'Light' : 'Dark';
  const bg = C(`Background/${theme}/Panel [mobile]`, false);
  const borderColor = C(`Hairline/${theme}/Transpar [mobile]`, false);
  const threeDDrawingBg = C('Gray/Light/6');

  const isThreeDDrawing = selectedImage?.extensionOriginal ? THREE_D_DRAWING_EXTENSIONS_TO_DISPLAY.includes(selectedImage?.extensionOriginal) : false;
  const imageBg = isThreeDDrawing ? threeDDrawingBg : C(`Background/${theme}/System [mobile]`, false);

  const { toggleDocTypeModal } = useManageDocTypeButton({
    images,
    img: selectedImage,
    onSuccessDocTypeChange,
    configStyles: {
      paddingLeft: scale(OP_INFO_TABS_WIDTH),
      ...(!isWeb && !isPhone ? { borderRadius: 33 } : {}),
    },
  });

  const getInfoPopupActionsConfig = ({ toggleAttachModal }: Partial<Pick<HeaderIconsProps, 'toggleAttachModal'>>): ImageHandlingPopupItem[] => [
    ...(isWeb
      ? [
          {
            title: i18n.t('Download'),
            icon: 'download' as const,
            onPress: () => download({ images, selectedIndex }),
            color: 'Label/Light/Primary',
            width: 15,
            height: 18,
          },
        ]
      : []),
    ...(!isWeb
      ? [
          {
            title: i18n.t('Share'),
            icon: 'share' as const,
            onPress: () => shareFile({ setLoading: () => {}, images, selectedIndex }),
            color: 'Label/Light/Primary',
            width: 15,
            height: 18,
          },
        ]
      : []),

    {
      title: i18n.t('Modify Attached To'),
      icon: 'clip' as const,
      onPress: toggleAttachModal || (() => {}),
      color: 'Label/Light/Primary',
      width: 15,
      height: 18,
    },
    {
      title: i18n.t('Modify Document Type'),
      icon: 'documentMore' as const,
      onPress: toggleDocTypeModal,
      color: 'Label/Light/Primary',
      width: 15,
      height: 18,
    },
    {
      title: i18n.t('Delete'),
      icon: 'remove' as const,
      onPress: () =>
        deletePrompt({
          images,
          selectedIndex,
          setSelectedIndex,
          deleteImage: removeImageDocument,
          onSuccessImageRemove,
          imageContext,
        }),
      color: 'Chart/Light/Warning',
      width: 15,
      height: 17,
    },
  ];

  return {
    selectedIndex,
    setSelectedIndex,
    cropImageDocument,
    rotateImageDocument,
    removeImageDocument,
    containerMeasurements,
    setContainerMeasurements,
    attachBlockMeasurements,
    setAttachBlockMeasurements,
    onSuccessImageUpload,
    onSuccessDocTypeChange,
    onSuccessImageRemove,
    IMAGE_PADDING_HORIZONTAL,
    IMAGE_PADDING_TOP,
    onClose: onCrossIconPress,
    selectedImage,
    bg,
    borderColor,
    imageBg,
    mode,
    setMode,
    getInfoPopupActionsConfig,
  };
};
